/**
 * View of one journal day: header with the date, composer and entries.
 * It is the main screen (Today, dateKey = today) and the calendar's day
 * view (/day/$date).
 */
import { createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate } from "@tanstack/solid-router";
import { ChevronLeft, ChevronRight } from "@fleet/icons";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import {
  addDaysToKey,
  buildEntryDataset,
  exportDataset,
  type ExportFormat,
  localDateKey,
} from "@journal/shared";
import { useAllEntryTagsQuery, useEntriesByDateQuery } from "@journal/shared/solid";
import { PageContainer } from "@fleet/ui";
import { OpenJournalIllustration } from "@/components/ui/illustrations";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { useI18n } from "@/providers/i18n-provider";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useCommandHandler } from "@fleet/shortcuts";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { closestSelector } from "@/lib/gestures/guards";
import { vibrate } from "@/lib/gestures/haptics";
import { DAY_SWIPE } from "@/lib/gestures/presets";
import { formatDayHeading } from "../format";
import { useJournalData } from "../hooks/use-journal-data";
import { EntryCard } from "./entry-card";
import { EntryComposer } from "./entry-composer";
import { EntryExportMenu } from "./entry-export-menu";
import { OnThisDay } from "./on-this-day";
import { StreakBadge } from "./streak-badge";

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

interface DayViewProps {
  dateKey: () => string;
}

export function DayView(props: DayViewProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { db, drizzle, userId, staleTime } = useJournalData();

  const isToday = () => props.dateKey() === localDateKey();

  const entries = useEntriesByDateQuery({
    db,
    drizzle,
    userId,
    staleTime,
    dateKey: () => props.dateKey(),
  });

  const allTags = useAllEntryTagsQuery({ db, drizzle, userId, staleTime });

  const tagsByEntry = createMemo(() => {
    const map = new Map<string, { tag_id: string; name: string | null }[]>();
    for (const row of allTags.data ?? []) {
      if (!row.entry_id) {
        continue;
      }
      const list = map.get(row.entry_id) ?? [];
      list.push({ tag_id: row.tag_id, name: row.name });
      map.set(row.entry_id, list);
    }
    return map;
  });

  // Export the day's shown entries (local-first: reads the already-loaded
  // live-query rows, no network) as CSV or PDF via the shared K1 kit.
  const delivery = useKernelService(EXPORT_DELIVERY);

  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    const rows = entries.data ?? [];
    const dataset = buildEntryDataset(rows, {
      title: t(msg`Journal entries`),
      subtitle: t(msg`Exported entries`),
    });
    void exportDataset(dataset, format, target);
  };

  const goToDay = (key: string) => {
    if (key === localDateKey()) {
      void navigate({ to: "/" });
    } else {
      void navigate({ to: "/day/$date", params: { date: key } });
    }
  };

  // ←/→ shortcuts for moving between days (no future beyond today).
  useCommandHandler(COMMAND_IDS.DAY_PREV, () => {
    goToDay(addDaysToKey(props.dateKey(), -1));
  });
  useCommandHandler(COMMAND_IDS.DAY_NEXT, () => {
    if (!isToday()) {
      goToDay(addDaysToKey(props.dateKey(), 1));
    }
  });

  // --- Swipe-to-page-day (carousel) ---------------------------------------
  // The page tracks the finger horizontally; on commit (or fling) the current
  // day slides out and navigation moves to the adjacent day. Swiping left pages
  // to the next day (blocked beyond today); swiping right pages to the previous
  // day. Physics (axis-lock, deadzone, fling) come from the shared recognizer,
  // so this matches the calendar's month swipe. The keyboard ←/→ shortcuts and
  // the header buttons remain untouched.
  const [surfaceRef, setSurfaceRef] = createSignal<HTMLDivElement>();
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);

  const surfaceWidth = () => surfaceRef()?.clientWidth ?? DAY_SWIPE.fallbackWidthPx;

  /** Animate a directional day change. delta>0 = next (current slides left). */
  const animateDay = (delta: number) => {
    if (delta === 0) {
      return;
    }
    // No future days beyond today.
    if (delta > 0 && isToday()) {
      snapBack();
      return;
    }
    const targetKey = addDaysToKey(props.dateKey(), delta);
    if (prefersReducedMotion()) {
      setDragX(0);
      goToDay(targetKey);
      return;
    }
    const width = surfaceWidth();
    setTransitioning(true);
    setDragX(delta > 0 ? -width : width); // current day leaves toward travel
    vibrate(DAY_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      goToDay(targetKey);
    }, DAY_SWIPE.transitionMs);
  };

  const snapBack = () => {
    setTransitioning(true);
    setDragX(0);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: DAY_SWIPE.thresholdPx,
    fling: { ...DAY_SWIPE.fling },
    canStart: ({ target }) =>
      // Don't hijack drags that begin on the composer, controls or links.
      !closestSelector(target, "textarea, input, select, button, a, [contenteditable]"),
    onStart: () => {
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      // Resist dragging into the future (left swipe on today's page).
      const dx = sample.dx < 0 && isToday() ? sample.dx * 0.2 : sample.dx;
      setDragX(dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        DAY_SWIPE.commitThresholdPx,
        surfaceWidth() * DAY_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next day; swipe right (dx>0) → previous day.
        animateDay(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
    },
    onCancel: () => {
      snapBack();
    },
  });

  const swipeHandlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  const surfaceTransform = () => `translate3d(${dragX()}px, 0, 0)`;
  const surfaceTransition = () =>
    transitioning() ? `transform ${DAY_SWIPE.transitionMs}ms ease-out` : "none";

  return (
    <PageContainer maxWidth="2xl" class="overflow-x-clip pb-16">
      {/* Swipe surface owns the horizontal gesture; `touch-action: pan-y`
          keeps native vertical scroll while we claim the horizontal axis. */}
      <div data-swipe-own style={{ "touch-action": "pan-y" }} {...swipeHandlers}>
        {/* Sliding layer: tracks the finger and animates day-to-day paging. */}
        <div
          ref={setSurfaceRef}
          class="flex flex-col gap-6 will-change-transform"
          style={{ transform: surfaceTransform(), transition: surfaceTransition() }}
          data-testid="day-swipe-surface"
        >
          <header class="flex flex-col gap-3 pt-2">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={t(msg`Previous day`)}
                  data-testid="day-prev"
                  onClick={() => {
                    goToDay(addDaysToKey(props.dateKey(), -1));
                  }}
                >
                  <ChevronLeft class="h-4 w-4" />
                </button>
                <Show when={!isToday()}>
                  <button
                    type="button"
                    class="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={t(msg`Next day`)}
                    data-testid="day-next"
                    onClick={() => {
                      goToDay(addDaysToKey(props.dateKey(), 1));
                    }}
                  >
                    <ChevronRight class="h-4 w-4" />
                  </button>
                </Show>
              </div>
              <div class="flex items-center gap-2">
                <EntryExportMenu onExport={handleExport} />
                <StreakBadge />
              </div>
            </div>

            <div>
              <Show when={isToday()}>
                <p class="text-sm font-medium tracking-wide text-primary">{t(msg`Today`)}</p>
              </Show>
              <h1
                class="font-display text-[1.75rem] font-semibold capitalize leading-tight text-foreground"
                data-testid="day-heading"
              >
                {formatDayHeading(props.dateKey(), locale())}
              </h1>
            </div>
          </header>

          <AppErrorBoundary
            label="editor"
            title={t(msg`The editor hit a snag`)}
            description={t(msg`Reload to keep writing — nothing you saved was lost.`)}
          >
            <EntryComposer dateKey={() => props.dateKey()} autofocus={isToday()} />
          </AppErrorBoundary>

          <Show
            when={(entries.data ?? []).length > 0}
            fallback={
              <Show when={!entries.isLoading}>
                <Show
                  when={isToday()}
                  fallback={
                    <p
                      class="px-1 font-serif text-[15px] italic leading-relaxed text-muted-foreground/70"
                      data-testid="day-empty"
                    >
                      {t(msg`No entries on this day.`)}
                    </p>
                  }
                >
                  <div
                    class="animate-fade-in flex flex-col items-center gap-3 px-1 py-6 text-center"
                    data-testid="day-empty"
                  >
                    <div class="animate-float text-primary">
                      <OpenJournalIllustration class="h-28 w-28" />
                    </div>
                    <p class="font-serif text-[15px] italic leading-relaxed text-muted-foreground/70">
                      {t(msg`Nothing written yet today. The page is yours.`)}
                    </p>
                  </div>
                </Show>
              </Show>
            }
          >
            <section class="flex flex-col gap-3" data-testid="day-entries">
              <For each={entries.data ?? []}>
                {(entry) => (
                  <EntryCard entry={entry} tags={() => tagsByEntry().get(entry.id) ?? []} />
                )}
              </For>
            </section>
          </Show>

          <Show when={isToday()}>
            <OnThisDay />
          </Show>
        </div>
      </div>
    </PageContainer>
  );
}
